import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authService from '../services/authService';
import { getProfileByUsername } from '../services/userService';
import { supabase, isDemoMode } from '../services/supabase';

const UserContext = createContext();

// ─── Demo accounts (used when Supabase credentials are not configured) ─────
const DEMO_USERS = {
  admin_demo: {
    id: 'demo-admin-001',
    email: 'admin@demo.local',
    username: 'admin_demo',
    firstName: 'Admin',
    lastName: 'Demo',
    role: 'admin',
    isActive: true,
  },
  student_demo: {
    id: 'demo-student-001',
    email: 'student@demo.local',
    username: 'student_demo',
    firstName: 'Alex',
    lastName: 'Student',
    role: 'student',
    isActive: true,
  },
};

const DEMO_PASSWORD = 'Demo1234';

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Build the currentUser object from a Supabase profile row
  const buildUser = (profile) => ({
    id: profile.id,
    email: profile.email,
    username: profile.username,
    firstName: profile.first_name,
    lastName: profile.last_name,
    role: profile.role,
    isActive: profile.is_active,
  });

  // On mount: restore session
  useEffect(() => {
    if (isDemoMode) {
      // In demo mode, check sessionStorage for a persisted demo user
      const saved = sessionStorage.getItem('demoUser');
      if (saved) {
        try { setCurrentUser(JSON.parse(saved)); } catch {}
      }
      setIsLoading(false);
      return;
    }

    let subscription;

    const init = async () => {
      const { session } = await authService.getSession();

      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (profile) {
          setCurrentUser(buildUser(profile));
        }
      }

      setIsLoading(false);

      subscription = authService.onAuthStateChange((event, newSession) => {
        if (event === 'SIGNED_IN' && newSession) {
          // Use setTimeout to escape the Supabase auth lock context.
          // The onAuthStateChange callback fires while the auth lock is held;
          // making a DB query here causes a deadlock that permanently blocks
          // all subsequent Supabase queries in the app.
          setTimeout(async () => {
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', newSession.user.id)
              .single();

            if (profile) setCurrentUser(buildUser(profile));
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setCurrentUser(null);
        }
      });
    };

    init();
    return () => { if (subscription) subscription.unsubscribe(); };
  }, []);

  const login = async ({ username, password }) => {
    if (isDemoMode) {
      const demoUser = DEMO_USERS[username];
      if (!demoUser || password !== DEMO_PASSWORD) {
        return { error: { message: 'Invalid username or password.' } };
      }
      setCurrentUser(demoUser);
      sessionStorage.setItem('demoUser', JSON.stringify(demoUser));
      return { error: null, user: demoUser };
    }

    // Live mode: resolve username → email
    const { data: profileLookup, error: lookupError } = await getProfileByUsername(username);

    if (lookupError || !profileLookup) {
      return { error: { message: 'Invalid username or password.' } };
    }

    const { data, error } = await authService.signIn({
      email: profileLookup.email,
      password,
    });

    if (error) return { error };

    const user = buildUser(data.profile);
    setCurrentUser(user);
    return { error: null, user };
  };

  const logout = async () => {
    if (isDemoMode) {
      sessionStorage.removeItem('demoUser');
      setCurrentUser(null);
      return;
    }
    await authService.signOut();
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    isLoading,
    login,
    logout,
    isDemoMode,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
